// @flow

// FX-296: a 401 on Insights (insights.<app host>) leaves a blank page; send the user to the app's sign-in instead.
// The app (sign-in, session) lives on the host without the "insights." prefix.

type Loc = { protocol: string, host: string, assign: (url: string) => void };

export function signInUrl(loc: Loc): string | null {
  if (!/^insights\./.test(loc.host)) {
    return null;
  }
  return `${loc.protocol}//${loc.host.replace(/^insights\./, '')}/`;
}

const COOKIES: string[] = ['session_r2d2', 'csrf_token']; // r2d2-api iam/auth/transport.go SessionCookieName, CsrfCookieName
const EXPIRED: string = 'expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';

// Clear what this origin stored, before leaving, or the stale value is sent again and the 401 repeats.
// A script can drop only what it may touch: storage, and cookies that are not HttpOnly (the browser ignores the rest).
export function clearStoredAuth(win: Object) {
  const host: string = win.location.host.split(':')[0];
  const appHost: string = host.replace(/^insights\./, '');
  const domains: Array<string | void> = [undefined, host, `.${host}`, appHost, `.${appHost}`];
  const clears: Array<() => void> = [(): void => win.localStorage.clear(), (): void => win.sessionStorage.clear()];
  clears.forEach((f: () => void) => {
    try { f(); } catch (e) { /* blocked storage: carry on */ }
  });
  try {
    COOKIES.forEach((name: string) => {
      domains.forEach((domain: string | void) => {
        // eslint-disable-next-line no-param-reassign
        win.document.cookie = `${name}=; ${EXPIRED}${domain ? `; domain=${domain}` : ''}`;
      });
    });
  } catch (e) { /* carry on */ }
}

const LOGOUT_URL: string = '/api/logout'; // r2d2-api iam/auth: expires the HttpOnly session cookie, which a script cannot (FX-304)
const LOGOUT_WAIT_MS: number = 2000;

// FX-304: ask r2d2 to expire the session cookie first (its logout answers 200 and expires it whatever the token's state), then clear what this
// origin stored and send the user to the app. A failed or slow logout call must not keep the user here: after the wait it carries on.
function leave(axiosInstance: Object, win: Object, url: string, waitMs: number) {
  let done: boolean = false;
  let timer: any = null;
  const finish: Function = () => {
    if (done) { return; }
    done = true;
    if (timer) { clearTimeout(timer); }
    clearStoredAuth(win);
    win.location.assign(url);
  };
  timer = setTimeout(finish, waitMs);
  try {
    Promise.resolve(axiosInstance.post(LOGOUT_URL, null, { withCredentials: true, timeout: waitMs })).then(finish, finish);
  } catch (e) {
    finish();
  }
}

export function installUnauthorizedRedirect(axiosInstance: Object, win: Object, options: Object = {}) {
  const waitMs: number = options.logoutWaitMs || LOGOUT_WAIT_MS;
  let redirected: boolean = false;
  axiosInstance.interceptors.response.use(
    (response: Object): Object => response,
    (error: Object): Promise<any> => {
      if (error && error.response && error.response.status === 401 && !redirected) {
        const url: string | null = signInUrl(win.location);
        if (url) {
          redirected = true; // set first: the logout call below may itself answer 401
          leave(axiosInstance, win, url, waitMs);
        }
      }
      return Promise.reject(error);
    },
  );
}
